/**
 * HOM Ring Builder — engagement ring finder.
 * Sidebar filter layout with collapsible groups, Cullen-inspired UX.
 *
 * The section renders every ring card and a JSON blob (#hom-rb-data) with
 * the stone prices, plating and tags each card needs. This script filters,
 * sorts and re-orders those server-rendered cards in place; it never builds
 * card markup and never fetches the catalogue.
 *
 * Filters are mirrored into the URL (?stone=lab&shape=Oval&sort=price-asc) so
 * a filtered view can be shared, bookmarked and restored with Back.
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════
     CONSTANTS
     ═══════════════════════════════════════ */

  var SHAPES   = ['Round','Oval','Emerald','Princess','Pear','Cushion','Elongated Cushion','Marquise'];
  var STYLES   = ['Trilogy','Solitaire','Halo','Duo','Bezel','East West'];
  var BANDS    = ['Plain','Pavé','Accents'];
  var PROFILES = ['High Set','Low Set'];
  var PRONGS   = ['3-Prong','4-Prong','6-Prong'];

  var STONES = [
    { value: 'moissanite', label: 'Moissanite' },
    { value: 'lab',        label: 'Lab-grown diamond' }
  ];

  // Plating over the 925 silver base, read from each ring's Plating option.
  var METALS = [
    { value: 'gold',    label: '21k gold',  color: '#e5c877' },
    { value: 'rhodium', label: 'Rhodium',   color: '#E8E8E8' }
  ];

  var SORTS = {
    'Recommended':        '',
    'Price: Low to High': 'price-asc',
    'Price: High to Low': 'price-desc',
    'Newest':             'newest'
  };

  /* ═══════════════════════════════════════
     SVG ICONS
     ═══════════════════════════════════════ */

  function getIcon(name) {
    var s = 1.1, h = 0.55, t = 0.733;
    switch (name) {

      /* ── Shapes ── */
      case 'Round':
        return '<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="26" stroke="currentColor" stroke-width="'+s+'"/><polygon points="32,14 47,20 50,32 47,44 32,50 17,44 14,32 17,20" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 14L32 6 M47 20L54 24 M50 32L58 32 M47 44L54 40 M32 50L32 58 M17 44L10 40 M14 32L6 32 M17 20L10 24" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 14L23 16 M32 14L41 16 M47 20L43 16 M47 20L50 26 M50 32L50 26 M50 32L50 38 M47 44L50 38 M47 44L43 48 M32 50L41 48 M32 50L23 48 M17 44L21 48 M17 44L14 38 M14 32L14 38 M14 32L14 26 M17 20L14 26 M17 20L21 16" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Oval':
        return '<svg viewBox="0 0 64 64" fill="none"><ellipse cx="32" cy="32" rx="20" ry="28" stroke="currentColor" stroke-width="'+s+'"/><ellipse cx="32" cy="32" rx="12" ry="18" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 14V4 M32 50V60 M44 32H52 M20 32H12" stroke="currentColor" stroke-width="'+s+'"/><path d="M40 19L48 10 M24 19L16 10 M40 45L48 54 M24 45L16 54" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 14L44 32L32 50L20 32Z" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Emerald':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M16 10H48L54 16V48L48 54H16L10 48V16L16 10Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M20 16H44L48 20V44L44 48H20L16 44V20L20 16Z" stroke="currentColor" stroke-width="'+s+'"/><rect x="24" y="24" width="16" height="16" stroke="currentColor" stroke-width="'+s+'"/><path d="M10 16L16 20 M54 16L48 20 M54 48L48 44 M10 48L16 44" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Princess':
        return '<svg viewBox="0 0 64 64" fill="none"><rect x="14" y="14" width="36" height="36" stroke="currentColor" stroke-width="'+s+'"/><rect x="24" y="24" width="16" height="16" stroke="currentColor" stroke-width="'+s+'" transform="rotate(45 32 32)"/><path d="M14 14L24 24 M50 14L40 24 M50 50L40 40 M14 50L24 40" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 14V21 M32 50V43 M14 32H21 M50 32H43" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Pear':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M32 60C45 60 54 48 54 38C54 24 32 4 32 4C32 4 10 24 10 38C10 48 19 60 32 60Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 16L42 36L32 50L22 36L32 16Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 4V16 M32 50V60 M54 38H42 M10 38H22" stroke="currentColor" stroke-width="'+s+'"/><path d="M22 16L14 24 M42 16L50 24 M22 50L16 54 M42 50L48 54" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Cushion':
        return '<svg viewBox="0 0 64 64" fill="none"><rect x="10" y="10" width="44" height="44" rx="12" stroke="currentColor" stroke-width="'+s+'"/><rect x="22" y="22" width="20" height="20" rx="4" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 10V22 M32 42V54 M10 32H22 M42 32H54" stroke="currentColor" stroke-width="'+s+'"/><path d="M14 14L22 22 M50 14L42 22 M50 50L42 42 M14 50L22 42" stroke="currentColor" stroke-width="'+s+'"/><path d="M22 22L32 32L42 22 M22 42L32 32L42 42" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Elongated Cushion':
        return '<svg viewBox="0 0 64 64" fill="none"><rect x="14" y="8" width="36" height="48" rx="10" stroke="currentColor" stroke-width="'+s+'"/><rect x="22" y="20" width="20" height="24" rx="4" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 8V20 M32 44V56 M14 32H22 M42 32H50" stroke="currentColor" stroke-width="'+s+'"/><path d="M18 12L22 20 M46 12L42 20 M46 52L42 44 M18 52L22 44" stroke="currentColor" stroke-width="'+s+'"/><path d="M22 20L32 32L42 20 M22 44L32 32L42 44" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Marquise':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M32 2 C 58 2, 58 62, 32 62 C 6 62, 6 2, 32 2 Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 14 L 40 32 L 32 50 L 24 32 Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M32 2 V 14 M 32 50 V 62" stroke="currentColor" stroke-width="'+s+'"/><path d="M14 32 H 24 M 40 32 H 50" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      /* ── Setting Styles ── */
      case 'Trilogy':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M6 38H14" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M50 38H58" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><circle cx="32" cy="36" r="10" stroke="currentColor" stroke-width="'+s+'"/><circle cx="32" cy="36" r="5" stroke="currentColor" stroke-width="'+t+'"/><circle cx="19" cy="38" r="5" stroke="currentColor" stroke-width="'+s+'"/><circle cx="45" cy="38" r="5" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Solitaire':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M4 36H22" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M42 36H60" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><circle cx="32" cy="36" r="10" stroke="currentColor" stroke-width="'+s+'"/><path d="M28 32L36 40 M36 32L28 40 M32 26V46 M22 36H42" stroke="currentColor" stroke-width="'+h+'"/><circle cx="32" cy="36" r="5" stroke="currentColor" stroke-width="'+s+'"/><path d="M24 28L26 30 M40 28L38 30 M24 44L26 42 M40 44L38 42" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Halo':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M4 36H16" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M48 36H60" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><circle cx="32" cy="36" r="14" stroke="currentColor" stroke-width="'+s+'"/><circle cx="32" cy="36" r="8" stroke="currentColor" stroke-width="'+s+'"/><path d="M28 32L36 40 M36 32L28 40 M32 28V44 M24 36H40" stroke="currentColor" stroke-width="'+h+'"/><circle cx="32" cy="24" r="1" fill="currentColor"/><circle cx="32" cy="48" r="1" fill="currentColor"/><circle cx="20" cy="36" r="1" fill="currentColor"/><circle cx="44" cy="36" r="1" fill="currentColor"/><circle cx="23" cy="27" r="1" fill="currentColor"/><circle cx="41" cy="27" r="1" fill="currentColor"/><circle cx="23" cy="45" r="1" fill="currentColor"/><circle cx="41" cy="45" r="1" fill="currentColor"/></svg>';

      case 'Duo':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M6 40 C 16 40, 20 34, 24 34" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M58 40 C 48 40, 44 34, 40 34" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M24 24 C 20 18, 16 20, 16 26 C 16 32, 22 36, 24 36 C 26 36, 32 32, 32 26 C 32 20, 28 18, 24 24 Z" stroke="currentColor" stroke-width="'+s+'" transform="rotate(-25 24 30)"/><ellipse cx="42" cy="28" rx="7" ry="10" stroke="currentColor" stroke-width="'+s+'" transform="rotate(25 42 28)"/></svg>';

      case 'Bezel':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M6 36H20" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M44 36H58" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><rect x="22" y="20" width="20" height="32" rx="3" stroke="currentColor" stroke-width="'+s+'"/><rect x="26" y="24" width="12" height="24" rx="1" stroke="currentColor" stroke-width="'+s+'"/><path d="M26 24L32 30 M38 24L32 30 M26 48L32 42 M38 48L32 42" stroke="currentColor" stroke-width="'+h+'"/></svg>';

      case 'East West':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M6 36H16" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M48 36H58" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><rect x="18" y="26" width="28" height="20" rx="5" stroke="currentColor" stroke-width="'+s+'"/><rect x="22" y="30" width="20" height="12" rx="2" stroke="currentColor" stroke-width="'+s+'"/><path d="M18 36H16 M46 36H48" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      /* ── Band Types ── */
      case 'Plain':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M10 36H54" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/><path d="M10 26V36" stroke="currentColor" stroke-width="'+s+'"/><path d="M54 26V36" stroke="currentColor" stroke-width="'+s+'"/><path d="M10 26H54" stroke="currentColor" stroke-width="'+s+'" stroke-linecap="round"/></svg>';

      case 'Pav\u00e9':
        return '<svg viewBox="0 0 64 64" fill="none"><rect x="10" y="28" width="44" height="10" rx="5" stroke="currentColor" stroke-width="'+s+'"/><circle cx="16" cy="33" r="2" stroke="currentColor" stroke-width="'+s+'"/><circle cx="24" cy="33" r="2" stroke="currentColor" stroke-width="'+s+'"/><circle cx="32" cy="33" r="2" stroke="currentColor" stroke-width="'+s+'"/><circle cx="40" cy="33" r="2" stroke="currentColor" stroke-width="'+s+'"/><circle cx="48" cy="33" r="2" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      case 'Accents':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M12 33L16 29L20 33L16 37L12 33Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M20 33L24 29L28 33L24 37L20 33Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M28 33L32 29L36 33L32 37L28 33Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M36 33L40 29L44 33L40 37L36 33Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M44 33L48 29L52 33L48 37L44 33Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M8 33H12 M52 33H56" stroke="currentColor" stroke-width="'+s+'"/></svg>';

      /* ── Setting Profiles ── */
      case 'High Set':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M8 52 C8 52 20 40 32 40 C44 40 56 52 56 52" stroke="currentColor" stroke-width="'+s+'"/><path d="M26 40 L29 26" stroke="currentColor" stroke-width="'+s+'"/><path d="M38 40 L35 26" stroke="currentColor" stroke-width="'+s+'"/><path d="M24 26 L32 18 L40 26 H24 Z" stroke="currentColor" stroke-width="'+s+'"/><path d="M24 26 L32 32 L40 26" stroke="currentColor" stroke-width="'+h+'"/></svg>';

      case 'Low Set':
        return '<svg viewBox="0 0 64 64" fill="none"><path d="M8 52 C8 52 20 40 32 40 C44 40 56 52 56 52" stroke="currentColor" stroke-width="'+s+'"/><path d="M24 32 L32 24 L40 32" stroke="currentColor" stroke-width="'+s+'"/><path d="M24 32 L32 40 L40 32" stroke="currentColor" stroke-width="'+s+'" stroke-dasharray="2 2"/></svg>';

      /* ── Prong Settings ── */
      case '3-Prong':
        return '<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="20" stroke="currentColor" stroke-width="'+s+'"/><circle cx="32" cy="14" r="2" fill="currentColor"/><circle cx="18" cy="44" r="2" fill="currentColor"/><circle cx="46" cy="44" r="2" fill="currentColor"/><circle cx="32" cy="32" r="1" fill="currentColor"/></svg>';

      case '4-Prong':
        return '<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="20" stroke="currentColor" stroke-width="'+s+'"/><circle cx="16" cy="16" r="2" fill="currentColor"/><circle cx="48" cy="16" r="2" fill="currentColor"/><circle cx="48" cy="48" r="2" fill="currentColor"/><circle cx="16" cy="48" r="2" fill="currentColor"/><circle cx="32" cy="32" r="1" fill="currentColor"/></svg>';

      case '6-Prong':
        return '<svg viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="20" stroke="currentColor" stroke-width="'+s+'"/><circle cx="32" cy="10" r="2" fill="currentColor"/><circle cx="32" cy="54" r="2" fill="currentColor"/><circle cx="12" cy="21" r="2" fill="currentColor"/><circle cx="52" cy="21" r="2" fill="currentColor"/><circle cx="12" cy="43" r="2" fill="currentColor"/><circle cx="52" cy="43" r="2" fill="currentColor"/><circle cx="32" cy="32" r="1" fill="currentColor"/></svg>';

      default:
        return '<svg viewBox="0 0 64 64" fill="none"><rect x="12" y="12" width="40" height="40" rx="4" stroke="currentColor" stroke-width="'+s+'" stroke-dasharray="4 4"/></svg>';
    }
  }

  /* ═══════════════════════════════════════
     TAG MATCHING (shape, band, profile, prong)
     ═══════════════════════════════════════ */

  function findTagValue(tags, validValues) {
    for (var i = 0; i < tags.length; i++) {
      var tl = tags[i].trim().toLowerCase();
      for (var j = 0; j < validValues.length; j++) {
        if (tl === validValues[j].toLowerCase()) return validValues[j];
      }
    }
    return '';
  }

  function findTagValueFuzzy(tags, validValues) {
    var exact = findTagValue(tags, validValues);
    if (exact) return exact;
    for (var j = 0; j < validValues.length; j++) {
      var v = validValues[j].toLowerCase();
      for (var i = 0; i < tags.length; i++) {
        if (tags[i].trim().toLowerCase().indexOf(v) !== -1) return validValues[j];
      }
    }
    return '';
  }

  /* ═══════════════════════════════════════
     FILTER GROUPS
     Each group: the options it offers, how an option is labelled, and whether
     a ring matches it. `param` is the URL key.
     ═══════════════════════════════════════ */

  function plain(list) {
    return list.map(function (v) { return { value: v, label: v }; });
  }

  var GROUPS = {
    stone:          { param: 'stone',   title: 'Stone',   options: STONES,          match: function (r, v) { return v === 'lab' ? r.lab > 0 : r.moi > 0; } },
    shape:          { param: 'shape',   title: 'Shape',   options: plain(SHAPES),   match: function (r, v) { return r.shape === v; } },
    metalType:      { param: 'plating', title: 'Plating', options: METALS,          match: function (r, v) { return v === 'gold' ? r.gold : r.rhodium; } },
    settingStyle:   { param: 'style',   title: 'Style',   options: plain(STYLES),   match: function (r, v) { return r.style === v; } },
    bandType:       { param: 'band',    title: 'Band',    options: plain(BANDS),    match: function (r, v) { return r.band === v; } },
    settingProfile: { param: 'profile', title: 'Profile', options: plain(PROFILES), match: function (r, v) { return r.profile === v; } },
    prongSetting:   { param: 'prong',   title: 'Prong',   options: plain(PRONGS),   match: function (r, v) { return r.prong === v; } }
  };
  var GROUP_KEYS = Object.keys(GROUPS);

  /* ═══════════════════════════════════════
     STATE
     ═══════════════════════════════════════ */

  var rings       = [];   // one per server-rendered card, in collection order
  var filters     = {};
  var currentSort = 'Recommended';
  var catalogueCounts = {}; // group -> value -> count with no filters applied
  var lastFocus   = null;

  /* ═══════════════════════════════════════
     FILTER & SORT LOGIC
     ═══════════════════════════════════════ */

  function activeFilterCount() {
    return GROUP_KEYS.filter(function (k) { return filters[k]; }).length;
  }

  // Does the ring pass every active filter, optionally ignoring one group?
  function passes(ring, exceptGroup) {
    for (var i = 0; i < GROUP_KEYS.length; i++) {
      var key = GROUP_KEYS[i];
      if (key === exceptGroup || !filters[key]) continue;
      if (!GROUPS[key].match(ring, filters[key])) return false;
    }
    return true;
  }

  // The price the card leads with: the chosen stone's, else the lowest shown.
  function displayPrice(ring) {
    if (filters.stone === 'lab') return ring.lab;
    if (filters.stone === 'moissanite') return ring.moi;
    var ps = [ring.moi, ring.lab].filter(function (p) { return p > 0; });
    return ps.length ? Math.min.apply(null, ps) : ring.priceMin;
  }

  function getFilteredRings() {
    var list = rings.filter(function (r) { return passes(r); });
    switch (currentSort) {
      case 'Price: Low to High':
        list.sort(function (a, b) { return displayPrice(a) - displayPrice(b) || a.order - b.order; });
        break;
      case 'Price: High to Low':
        list.sort(function (a, b) { return displayPrice(b) - displayPrice(a) || a.order - b.order; });
        break;
      case 'Newest':
        list.sort(function (a, b) { return b.id - a.id; });
        break;
      default:
        list.sort(function (a, b) { return a.order - b.order; });
    }
    return list;
  }

  // How many rings each option would show, given the other active filters.
  function optionCount(groupKey, value) {
    var n = 0;
    for (var i = 0; i < rings.length; i++) {
      if (GROUPS[groupKey].match(rings[i], value) && passes(rings[i], groupKey)) n++;
    }
    return n;
  }

  function toggleFilter(name, value) {
    filters[name] = (filters[name] === value) ? undefined : value;
    updateUI();
  }

  function clearAllFilters() {
    filters = {};
    updateUI();
  }

  /* ═══════════════════════════════════════
     URL STATE
     ═══════════════════════════════════════ */

  function readURL() {
    var params = new URLSearchParams(window.location.search);
    GROUP_KEYS.forEach(function (key) {
      var raw = params.get(GROUPS[key].param);
      if (!raw) return;
      var hit = GROUPS[key].options.filter(function (o) {
        return o.value.toLowerCase() === raw.toLowerCase();
      })[0];
      if (hit) filters[key] = hit.value;
    });
    var sortParam = params.get('sort');
    Object.keys(SORTS).forEach(function (label) {
      if (SORTS[label] && SORTS[label] === sortParam) currentSort = label;
    });
  }

  function writeURL() {
    var params = new URLSearchParams(window.location.search);
    GROUP_KEYS.forEach(function (key) {
      if (filters[key]) params.set(GROUPS[key].param, filters[key]);
      else params.delete(GROUPS[key].param);
    });
    if (SORTS[currentSort]) params.set('sort', SORTS[currentSort]);
    else params.delete('sort');
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    if (url !== window.location.pathname + window.location.search + window.location.hash) {
      window.history.replaceState(null, '', url);
    }
  }

  /* ═══════════════════════════════════════
     RENDERING — FILTERS
     ═══════════════════════════════════════ */

  function esc(str) {
    return String(str == null ? '' : str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function buttonAttrs(groupKey, option, count) {
    var active = filters[groupKey] === option.value;
    return ' type="button" data-filter-name="' + esc(groupKey) + '" data-filter-value="' + esc(option.value) + '"' +
      ' aria-pressed="' + active + '"' +
      ' aria-label="' + esc(option.label) + ', ' + count + ' ring' + (count === 1 ? '' : 's') + '"' +
      (count === 0 && !active ? ' disabled' : '');
  }

  function classes(base, groupKey, option, count) {
    var active = filters[groupKey] === option.value;
    return base + (active ? ' hom-rb__fbtn--active' : '') + (count === 0 && !active ? ' hom-rb__fbtn--empty' : '');
  }

  function renderGroup(groupKey) {
    var el = document.querySelector('[data-filter="' + groupKey + '"]');
    if (!el) return;
    var group = GROUPS[groupKey];

    // An option no ring in the collection has is a dead end; leave it out.
    var options = group.options.filter(function (o) {
      return catalogueCounts[groupKey][o.value] > 0 || filters[groupKey] === o.value;
    });

    el.innerHTML = options.map(function (o) {
      var count = optionCount(groupKey, o.value);
      var countHTML = '<span class="hom-rb__fbtn-count" aria-hidden="true">' + count + '</span>';

      if (groupKey === 'stone') {
        return '<button class="' + classes('hom-rb__fbtn hom-rb__fbtn--row', groupKey, o, count) + '"' + buttonAttrs(groupKey, o, count) + '>' +
          '<span class="hom-rb__fbtn-label">' + esc(o.label) + '</span>' + countHTML + '</button>';
      }
      if (groupKey === 'metalType') {
        return '<button class="' + classes('hom-rb__fbtn hom-rb__fbtn--metal', groupKey, o, count) + '"' + buttonAttrs(groupKey, o, count) + '>' +
          '<span class="hom-rb__metal-dot" style="background:' + o.color + '" aria-hidden="true"></span>' +
          '<span class="hom-rb__fbtn-label">' + esc(o.label) + '</span>' + countHTML + '</button>';
      }
      return '<button class="' + classes('hom-rb__fbtn' + (groupKey === 'bandType' ? ' hom-rb__fbtn--band' : ''), groupKey, o, count) + '"' + buttonAttrs(groupKey, o, count) + '>' +
        '<span class="hom-rb__fbtn-icon" aria-hidden="true">' + getIcon(o.value) + '</span>' +
        '<span class="hom-rb__fbtn-label">' + esc(o.label) + '</span>' + countHTML + '</button>';
    }).join('');
  }

  function renderFilters() {
    GROUP_KEYS.forEach(renderGroup);
  }

  /* ═══════════════════════════════════════
     RENDERING — ACTIVE PILLS
     ═══════════════════════════════════════ */

  function labelFor(groupKey, value) {
    var hit = GROUPS[groupKey].options.filter(function (o) { return o.value === value; })[0];
    return hit ? hit.label : value;
  }

  function renderActivePills() {
    var container = document.getElementById('hom-rb-active');
    if (!container) return;
    container.innerHTML = GROUP_KEYS.filter(function (k) { return filters[k]; }).map(function (key) {
      var label = labelFor(key, filters[key]);
      return '<button type="button" class="hom-rb__pill" data-pill-filter="' + esc(key) + '" aria-label="Remove ' + esc(label) + ' filter">' +
        esc(label) + ' <span class="hom-rb__pill-x" aria-hidden="true">&times;</span></button>';
    }).join('');
  }

  /* ═══════════════════════════════════════
     RENDERING — GRID (re-orders the server-rendered cards)
     ═══════════════════════════════════════ */

  function applyStoneToCard(ring) {
    var card = ring.el;
    var stone = filters.stone;
    var lines = card.querySelectorAll('.hom-rb__price-line[data-stone]');
    for (var i = 0; i < lines.length; i++) {
      lines[i].hidden = !!stone && lines[i].getAttribute('data-stone') !== stone;
    }
    // Land on the product page with the chosen stone already selected.
    var variant = stone === 'lab' ? ring.labVariant : stone === 'moissanite' ? ring.moiVariant : '';
    card.setAttribute('href', variant ? ring.url + '?variant=' + variant : ring.url);
  }

  function renderGrid(list) {
    var grid = document.getElementById('hom-rb-grid');
    if (!grid) return;

    var countEl = document.getElementById('hom-rb-count');
    if (countEl) countEl.textContent = 'Showing ' + list.length + ' ring' + (list.length === 1 ? '' : 's');
    var doneCount = document.getElementById('hom-rb-done-count');
    if (doneCount) doneCount.textContent = String(list.length);

    var frag = document.createDocumentFragment();
    list.forEach(function (ring) {
      applyStoneToCard(ring);
      frag.appendChild(ring.el);
    });

    if (list.length === 0) {
      var msg = document.createElement('div');
      msg.className = 'hom-rb__message';
      msg.innerHTML = '<h2>No rings match your filters</h2>' +
        '<p>Try removing a filter to see more designs.</p>' +
        '<button type="button" class="hom-rb__message-btn" id="hom-rb-clear-msg">Clear all filters</button>';
      frag.appendChild(msg);
    }

    grid.replaceChildren(frag);
  }

  /* ═══════════════════════════════════════
     UI UPDATE ORCHESTRATOR
     ═══════════════════════════════════════ */

  function updateUI() {
    var count = activeFilterCount();

    renderFilters();
    renderActivePills();

    var clearBtn = document.getElementById('hom-rb-clear');
    if (clearBtn) clearBtn.classList.toggle('hom-rb__clear-btn--visible', count > 0);

    var badge = document.getElementById('hom-rb-filter-badge');
    if (badge) {
      badge.textContent = String(count);
      badge.classList.toggle('hom-rb__filter-badge--visible', count > 0);
    }

    ['hom-rb-sort', 'hom-rb-sort-mobile'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.value = currentSort;
    });

    renderGrid(getFilteredRings());
    writeURL();
  }

  /* ═══════════════════════════════════════
     SIDEBAR (mobile drawer)
     ═══════════════════════════════════════ */

  function openSidebar() {
    var sidebar = document.getElementById('hom-rb-sidebar');
    var overlay = document.getElementById('hom-rb-overlay');
    var toggle = document.getElementById('hom-rb-filter-toggle');
    lastFocus = document.activeElement;
    if (sidebar) sidebar.classList.add('hom-rb__sidebar--open');
    if (overlay) overlay.classList.add('hom-rb__overlay--visible');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    var close = document.getElementById('hom-rb-sidebar-close');
    if (close) close.focus();
  }

  function closeSidebar() {
    var sidebar = document.getElementById('hom-rb-sidebar');
    var overlay = document.getElementById('hom-rb-overlay');
    var toggle = document.getElementById('hom-rb-filter-toggle');
    if (!sidebar || !sidebar.classList.contains('hom-rb__sidebar--open')) return;
    sidebar.classList.remove('hom-rb__sidebar--open');
    if (overlay) overlay.classList.remove('hom-rb__overlay--visible');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ═══════════════════════════════════════
     COLLAPSIBLE GROUPS
     ═══════════════════════════════════════ */

  function toggleGroup(groupEl) {
    var open = groupEl.getAttribute('data-group-open') !== 'true';
    var body = groupEl.querySelector('.hom-rb__group-body');
    var toggle = groupEl.querySelector('.hom-rb__group-toggle');
    groupEl.setAttribute('data-group-open', String(open));
    if (body) body.hidden = !open;
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
  }

  /* ═══════════════════════════════════════
     EVENTS
     ═══════════════════════════════════════ */

  function setupEvents() {
    var sidebar = document.getElementById('hom-rb-sidebar');
    if (sidebar) {
      sidebar.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-filter-name]');
        if (btn) {
          if (btn.disabled) return;
          toggleFilter(btn.dataset.filterName, btn.dataset.filterValue);
          return;
        }
        var toggle = e.target.closest('.hom-rb__group-toggle');
        if (toggle) {
          var group = toggle.closest('.hom-rb__filter-group');
          if (group) toggleGroup(group);
        }
      });
    }

    var activeContainer = document.getElementById('hom-rb-active');
    if (activeContainer) {
      activeContainer.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-pill-filter]');
        if (pill) {
          filters[pill.dataset.pillFilter] = undefined;
          updateUI();
        }
      });
    }

    var clearBtn = document.getElementById('hom-rb-clear');
    if (clearBtn) clearBtn.addEventListener('click', clearAllFilters);

    var grid = document.getElementById('hom-rb-grid');
    if (grid) {
      grid.addEventListener('click', function (e) {
        if (e.target && e.target.id === 'hom-rb-clear-msg') clearAllFilters();
      });
    }

    ['hom-rb-sort', 'hom-rb-sort-mobile'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.addEventListener('change', function () {
        currentSort = el.value;
        updateUI();
      });
    });

    var filterToggle = document.getElementById('hom-rb-filter-toggle');
    if (filterToggle) filterToggle.addEventListener('click', openSidebar);

    ['hom-rb-sidebar-close', 'hom-rb-done', 'hom-rb-overlay'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.addEventListener('click', closeSidebar);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeSidebar();
    });
  }

  /* ═══════════════════════════════════════
     INIT
     ═══════════════════════════════════════ */

  function readRings() {
    var dataEl = document.getElementById('hom-rb-data');
    var data = [];
    try { data = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (err) {
      console.error('Ring builder: bad ring data', err);
    }
    var cards = {};
    document.querySelectorAll('#hom-rb-grid [data-ring-id]').forEach(function (el) {
      cards[el.getAttribute('data-ring-id')] = el;
    });

    return data.map(function (d, i) {
      var tags = d.tags || [];
      return {
        el: cards[String(d.id)],
        order: i,
        id: Number(d.id),
        url: d.url,
        style: d.style || '',
        shape: findTagValueFuzzy(tags, SHAPES),
        band: findTagValue(tags, BANDS),
        profile: findTagValue(tags, PROFILES),
        prong: findTagValue(tags, PRONGS),
        gold: !!d.gold,
        rhodium: !!d.rhodium,
        moi: Number(d.moi) || 0,
        lab: Number(d.lab) || 0,
        priceMin: Number(d.priceMin) || 0,
        moiVariant: d.moiVariant || '',
        labVariant: d.labVariant || ''
      };
    }).filter(function (r) { return r.el; });
  }

  function init() {
    if (!document.getElementById('hom-ring-builder')) return;

    rings = readRings();
    GROUP_KEYS.forEach(function (key) {
      catalogueCounts[key] = {};
      GROUPS[key].options.forEach(function (o) {
        catalogueCounts[key][o.value] = rings.filter(function (r) { return GROUPS[key].match(r, o.value); }).length;
      });
    });

    readURL();
    setupEvents();
    updateUI();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
